import { ContactDetails, MedicalProfile, AuditLog } from '../DynamoTypes';
import { PrimaryRecordVisit } from '../MedicalDataTypes';
import {
    OrganizationUserRole,
    OrganizationGroup,
    OrganizationInvitationDefinition,
    Organization,
    OrganizationUserInvitation,
    OrganizationClientRecord,
    OrganizationClientInvitation,
    OrganizationClientRecordBaseFields,
    OrganizationClientRecordClientOrganizerDetails,
    OrganizationClientRecordClientDetails,
    OrganizationStripeAccountStatus,
    OrganizationInvoiceForStatus,
    OrganizationInvoice,
    OrganizationInvoiceItem,
    OrganizationInvoiceStatus,
} from '../OrganizationTypes';
import { AppPermissions } from '../abac';
import { ResourceImageCreate } from './medicalDataService';
import { CareTeamMemberDetails, UpsertCareTeamMember, CreateMedicalProfileRequest } from './medicalProfileService';
import { WebStripeClientType } from './paymentService';
import { AvatarChange, GetAvatarResponse } from './sharedTypes';

export type OrganizationContactDetails = Pick<
    ContactDetails,
    'address1' | 'address2' | 'city' | 'state' | 'zipCode' | 'email' | 'phoneNumber' | 'faxNumber' | 'url'
>;

export type OrganizationDetails = Omit<Organization, 'hubspotCompanyId'> & {
    contactDetails: OrganizationContactDetails;
};

export type CreateOrganizationRequest = {
    name: string;
    description: string;
    avatar: AvatarChange;
    logo: AvatarChange;
    isPubliclyListed?: boolean;
    brandingPrimaryColor?: string;
    brandingSecondaryColor?: string;
    sessionTimeoutMinutes?: number | null;
} & OrganizationContactDetails;

export type CreateOrganizationResponse = OrganizationDetails;

export type UpdateOrganizationRequest = Partial<CreateOrganizationRequest>;

export type UpdateOrganizationResponse = OrganizationDetails;

export type GetOrganizationResponse = OrganizationDetails;

export type GetOrganizationAvatarOrLogoResponse = GetAvatarResponse | null;

export type GetOrganizationMeResponse = {
    organization: OrganizationDetails;
    role: OrganizationUserRole;
    isBillingAdmin?: boolean;
} | null;

export type InvitationDefinitionFileUpsert = {
    description: string;
    name: string;
    contentType?: string;
    fileId?: string;
    tempFileId?: string;
};

export type InvitationDefinitionUpsert = Pick<OrganizationInvitationDefinition, 'message' | 'name' | 'inputs'> & {
    requestedProfilePermissions: AppPermissions;
    files: InvitationDefinitionFileUpsert[];
    organizationInvitationDefinitionId?: string;
};

export type ListDefinitionsResponse = {
    invitationDefinitions: OrganizationInvitationDefinition[];
};

export type GetInvitationDefinitionFileDownloadUrlResponse = {
    downloadUrl: string;
};

export type GetInvitationDefinitionResponse = OrganizationInvitationDefinition & {
    requestedProfilePermissions: AppPermissions | null;
};

export type UpdateInvitationDefinitionRequest = InvitationDefinitionUpsert;

export type CreateInvitationDefinitionRequest = InvitationDefinitionUpsert;
export type OrganizationGroupContactDetails = Pick<
    ContactDetails,
    'address1' | 'address2' | 'city' | 'state' | 'zipCode'
>;

export type OrganizationGroupDetails = OrganizationGroup & {
    contactDetails: OrganizationGroupContactDetails;
};

export type CreateOrganizationGroupRequest = {
    name: string;
    description?: string;
} & OrganizationGroupContactDetails;

export type CreateOrganizationGroupResponse = OrganizationGroupDetails;

export type UpdateOrganizationGroupRequest = Partial<CreateOrganizationGroupRequest>;

export type UpdateOrganizationGroupResponse = OrganizationGroupDetails;

export type GetOrganizationGroupResponse = OrganizationGroupDetails;

export type ListOrganizationGroupsResponse = {
    items: OrganizationGroupDetails[];
};

export type OrganizationUserDetails = {
    organizationUserId: string;
    role: OrganizationUserRole;
    isBillingAdmin?: boolean;
    organizationId: string;
    user: {
        userId: string;
        firstName: string;
        lastName: string;
        email: string;
    };
};

export type ListOrganizationUsersResponse = {
    items: OrganizationUserDetails[];
};

export type GetOrganizationUserResponse = OrganizationUserDetails;

export type GetOrganizationUserAvatarResponse = GetAvatarResponse | null;

export type UpdateOrganizationUserIsBillingAdminRequest = {
    isBillingAdmin: boolean;
};

export type UpdateOrganizationUserRoleRequest = {
    role: OrganizationUserRole;
};

export type UpdateOrganizationUserResponse = OrganizationUserDetails;

export type OrganizationMedicalProfileContactDetails = Pick<
    ContactDetails,
    | 'firstName'
    | 'lastName'
    | 'preferredFirstName'
    | 'dob'
    | 'sex'
    | 'gender'
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'zipCode'
    | 'email'
    | 'phoneNumber'
    | 'faxNumber'
>;

export type OrganizationMedicalProfileDetails = MedicalProfile & {
    contactDetails: OrganizationMedicalProfileContactDetails;
};

export type OrganizationMedicalProfileDetailsWithOrganizationGroups = OrganizationMedicalProfileDetails & {
    organizationGroups: OrganizationGroupDetails[];
};

export type ListOrganizationMedicalProfilesResponse = {
    items: OrganizationMedicalProfileDetailsWithOrganizationGroups[];
};

export type GetOrganizationMedicalProfileResponse = OrganizationMedicalProfileDetailsWithOrganizationGroups;

export type ListOrganizationGroupClientRecordsResponse = {
    items: OrganizationClientRecordDetails[];
};

export type UpdateOrganizationGroupClientRecordsRequest = {
    organizationClientRecordIds: string[];
};

export type UpdateOrganizationGroupClientRecordsResponse = {
    items: OrganizationClientRecordDetails[];
};

export type BulkAddOrganizationGroupClientRecordsRequest = {
    organizationGroupIds: string[];
    organizationClientRecordIds: string[];
};

export type ListOrganizationGroupUsersResponse = {
    items: OrganizationUserDetails[];
};

export type UpdateOrganizationGroupUsersRequest = {
    organizationUserIds: string[];
};

export type UpdateOrganizationGroupUsersResponse = {
    items: OrganizationUserDetails[];
};

export type ListOrganizationMedicalProfileOrganizationGroupsResponse = {
    items: OrganizationGroupDetails[];
};

export type ListOrganizationClientRecordOrganizationGroupsResponse = {
    items: OrganizationGroupDetails[];
};

export type UpdateOrganizationMedicalProfileOrganizationGroupsRequest = {
    organizationGroupIds: string[];
};

export type UpdateOrganizationMedicalProfileOrganizationGroupsResponse = {
    items: OrganizationGroupDetails[];
};

export type UpdateOrganizationClientRecordOrganizationGroupsRequest = {
    organizationGroupIds: string[];
};

export type UpdateOrganizationClientRecordOrganizationGroupsResponse = {
    items: OrganizationGroupDetails[];
};

export type ListOrganizationUserOrganizationGroupsResponse = {
    items: OrganizationGroupDetails[];
};

export type UpdateOrganizationUserOrganizationGroupsRequest = {
    organizationGroupIds: string[];
};

export type UpdateOrganizationUserOrganizationGroupsResponse = {
    items: OrganizationGroupDetails[];
};

export type OrganizationUserInvitationDetails = Omit<OrganizationUserInvitation, 'inviteeUserId'>;

export type CreateOrganizationUserInvitationRequest = Pick<
    OrganizationUserInvitationDetails,
    'inviteeEmail' | 'inviteeRole' | 'message' | 'inviteeFirstName' | 'inviteeLastName'
>;

export type CreateOrganizationUserInvitationResponse = OrganizationUserInvitationDetails;

export type ListOrganizationUserInvitationsResponse = {
    items: OrganizationUserInvitationDetails[];
};

export type GetOrganizationUserInvitationResponse = OrganizationUserInvitationDetails;

export type MaskedOrganizationDetails = Omit<OrganizationDetails, 'paymentStatus' | 'ownerUserId'>;

export type GetOrganizationUserInvitationMeResponse = {
    items: (OrganizationUserInvitationDetails & {
        organization: MaskedOrganizationDetails;
    })[];
};

export type OrganizationUserInvitationResponseRequest = {
    response: 'accept' | 'reject';
};

export type GetOrganizationUserInvitationOrganizationAvatarOrLogoResponse = GetAvatarResponse | null;

// Exclude the `stripeCustomerId` and `clientOrganizerUserId` fields from the response.
// They are not needed by the client, so they are not exposed unnecessarily.
export type OrganizationClientRecordDetails = Omit<
    OrganizationClientRecord,
    'stripeCustomerId' | 'clientOrganizerUserId'
>;

export type CreateOrganizationClientRecordRequest = Pick<
    OrganizationClientRecordBaseFields,
    'clientOrganizerDetails' | 'clientDetails'
>;

export type CreateOrganizationClientRecordResponse = OrganizationClientRecordDetails;

export type BulkCreateOrganizationClientRecordsRequest = {
    organizationClientRecords: CreateOrganizationClientRecordRequest[];
};

export type BulkCreateOrganizationClientRecordsResponse = {
    items: OrganizationClientRecordDetails[];
};

export type UpdateOrganizationClientRecordRequest = Partial<CreateOrganizationClientRecordRequest> & {
    status?: 'not-invited' | 'archived';
};

export type UpdateOrganizationClientRecordResponse = OrganizationClientRecordDetails;

export type GetOrganizationClientRecordResponse = OrganizationClientRecordDetails;

export type OrganizationClientRecordDetailsWithPastRecordsAndOrganizationGroups = OrganizationClientRecordDetails & {
    pastOrganizationClientRecords: OrganizationClientRecordDetails[];
    organizationGroups?: OrganizationGroupDetails[];
};

export type ListOrganizationClientRecordsResponse = {
    items: OrganizationClientRecordDetailsWithPastRecordsAndOrganizationGroups[];
};

export type ReengageOrganizationClientRecordResponse = OrganizationClientRecordDetails;

export type GetOrganizationClientRecordsForMedicalProfileResponse = {
    items: OrganizationClientRecordDetails[];
};

export type GetOrganizationClientRecordInvitationFileDownloadUrlResponse = {
    downloadUrl: string;
};

export type OrganizationClientInvitationDetails = Omit<OrganizationClientInvitation, 'clientOrganizerUserId'>;

type UpsertOrganizationClientInvitationRequest = Pick<
    OrganizationClientInvitationDetails,
    | 'organizationInvitationDefinitionId'
    | 'destinationOrganizationGroupIds'
    | 'clientOrganizerEmail'
    | 'clientOrganizerFirstName'
    | 'clientOrganizerLastName'
    | 'clientFirstName'
    | 'clientLastName'
    | 'clientDOB'
    | 'expirationDateTime'
> & {
    requestedProfilePermissions: AppPermissions | null;
};

export type CreateOrganizationClientInvitationRequest = UpsertOrganizationClientInvitationRequest & {
    // TODO: Make this required once the frontend is updated to always send it
    provisionMedicalProfile?: boolean;
};

export type CreateOrganizationClientInvitationResponse = OrganizationClientInvitationDetails;

export type UpdateOrganizationClientInvitationRequest = UpsertOrganizationClientInvitationRequest;

export type UpdateOrganizationClientInvitationResponse = OrganizationClientInvitationDetails;

export type ListOrganizationClientInvitationsResponse = {
    items: OrganizationClientInvitationDetails[];
};

export type GetOrganizationClientInvitationResponse = OrganizationClientInvitationDetails & {
    requestedProfilePermissions: AppPermissions | null;
};

export type GetOrganizationClientInvitationOrganizationAvatarOrLogoResponse = GetAvatarResponse | null;

export type GetOrganizationClientInvitationMeResponse = {
    items: (OrganizationClientInvitationDetails & {
        organization: MaskedOrganizationDetails;
        requestedProfilePermissions: AppPermissions | null;
        /**
         * The ID of the medical profile that the organization has provisioned for the client
         */
        provisionedMedicalProfileId: string | null;
    })[];
};

export type OrganizationClientInvitationFileInput = {
    fileInputId: string;
    tempFileId: string;
    contentType: string;
};

type BaseOrganizationClientInvitationAcceptResponse = {
    response: 'accept';
    appPermissions: AppPermissions;
    fileInputs?: OrganizationClientInvitationFileInput[];
};

export type OrganizationClientInvitationAcceptMedicalProfileIdResponse =
    BaseOrganizationClientInvitationAcceptResponse & {
        medicalProfileId: string;
    };

export type OrganizationClientInvitationAcceptCreateMedicalProfileRequestResponse =
    BaseOrganizationClientInvitationAcceptResponse & {
        createMedicalProfileRequest: CreateMedicalProfileRequest;
    };

export type OrganizationClientInvitationAcceptResponse =
    | OrganizationClientInvitationAcceptMedicalProfileIdResponse
    | OrganizationClientInvitationAcceptCreateMedicalProfileRequestResponse;

export type OrganizationClientInvitationRejectResponse = {
    response: 'reject';
};

export type OrganizationClientInvitationResponseRequest =
    | OrganizationClientInvitationAcceptResponse
    | OrganizationClientInvitationRejectResponse;

export type OrganizationClientInvitationResponseRejectResponse = {
    response: 'reject';
};

export type OrganizationClientInvitationResponseAcceptResponse = {
    response: 'accept';
    medicalProfileId: string;
};

export type OrganizationClientInvitationResponseResponse =
    | OrganizationClientInvitationResponseRejectResponse
    | OrganizationClientInvitationResponseAcceptResponse;

export type GetOrganizationClientInvitationFileDownloadUrlResponse = {
    downloadUrl: string;
};

export type CreateOrganizationClientInvitationForOrganizationClientRecordRequest = Pick<
    OrganizationClientInvitationDetails,
    'organizationInvitationDefinitionId' | 'destinationOrganizationGroupIds' | 'expirationDateTime'
> & {
    clientDetails?: Partial<OrganizationClientRecordClientDetails>;
    clientOrganizerDetails?: Partial<OrganizationClientRecordClientOrganizerDetails>;
    requestedProfilePermissions: AppPermissions | null;
    // TODO: Make this required once the frontend is updated to always send it
    provisionMedicalProfile?: boolean;
};

export type CreateOrganizationClientInvitationForOrganizationClientRecordResponse = OrganizationClientInvitationDetails;

export type GetOrganizationClientInvitationForOrganizationClientRecordResponse = OrganizationClientInvitationDetails;

export type OrganizationCareTeamMemberDetails = Omit<CareTeamMemberDetails, 'references'>;

export type CreateOrganizationCareTeamMemberRequest = UpsertCareTeamMember;

export type CreateOrganizationCareTeamMemberResponse = OrganizationCareTeamMemberDetails;

export type GetOrganizationCareTeamMemberResponse = OrganizationCareTeamMemberDetails;

export type GetOrganizationCareTeamMemberAvatarResponse = GetAvatarResponse | null;

export type GetOrganizationCareTeamMemberInsuranceCardImageResponse = GetAvatarResponse | null;

export type ListOrganizationCareTeamMembersResponse = {
    items: OrganizationCareTeamMemberDetails[];
};

export type UpdateOrganizationCareTeamMemberRequest = UpsertCareTeamMember;

export type UpdateOrganizationCareTeamMemberResponse = OrganizationCareTeamMemberDetails;

export type CopyOrganizationCareTeamMemberToOrganizationMedicalProfilesRequest = {
    destinationMedicalProfileIds: string[];
};

export type CopyOrganizationCareTeamMemberToOrganizationMedicalProfilesResponse = {
    noAccessMedicalProfileIds: string[];
};

export type PersistFileToOrganizationMedicalProfilesRequest = {
    tempFileId: string;
    fileName: string;
    contentType: string;
    destinationMedicalProfileIds: string[];
};

export type PersistFilesToOrganizationMedicalProfilesRequest = {
    tempFiles: {
        tempFileId: string;
        fileName: string;
        contentType: string;
    }[];
    destinationMedicalProfileIds: string[];
};

export type PersistFilesToOrganizationMedicalProfilesResponse = {
    noAccessMedicalProfileIds: string[];
};

export type PersistVisitToOrganizationMedicalProfilesRequest = {
    destinationMedicalProfileIds: string[];
    /** The visit to persist; a recurrence rule expands server-side per destination profile. */
    resource: Omit<PrimaryRecordVisit, 'id'>;
    images: ResourceImageCreate[];
    /** Org note to copy onto each created visit as a 'visit-resource' note. */
    sourceNoteId?: string;
};

export type PersistVisitToOrganizationMedicalProfilesResponse = {
    noAccessMedicalProfileIds: string[];
};

export type ValidAuditLogsDetails = {
    status: 'valid';
    auditLog: AuditLog;
};

export type ListOrganizationClientRecordAuditLogsResponse = {
    items: ValidAuditLogsDetails[];
};

export type EntityAuditLogListItem = AuditLog & {
    auditLogDetails: string;
};

export type ListOrganizationEntityAuditLogsResponse = {
    items: EntityAuditLogListItem[];
};

export type OrganizationMaxMedicalProfileMonthlyUsage = {
    /**
     * Of the form "YYYY-MM"
     */
    month: string;
    maxMedicalProfileCount: number;
};

export type GetOrganizationMaxMedicalProfileUsageResponse = {
    currentMonthMaxMedicalProfileCount: number;
    pastMonthsMaxMedicalProfileCounts: OrganizationMaxMedicalProfileMonthlyUsage[];
};

export type GetOrganizationAllowedMedicalProfileUsageResponse =
    | {
          type: 'unlimited';
      }
    | {
          type: 'limited';
          maxAllowedMedicalProfileCount: number;
          currentMedicalProfileCount: number;
      };

export type GetOrganizationMedicalProfileFolderIdResponse = {
    medicalProfileFileFolderId: string | null;
};

export type SetupOrganizationPaymentIntegrationRequest = {
    client: WebStripeClientType;
};

export type SetupOrganizationPaymentIntegrationResponse = {
    onboardingUrl: string;
};

export type GetOrganizationPaymentIntegrationStatusResponse = {
    status: OrganizationStripeAccountStatus | 'not-setup';
};

export type GetOrganizationPaymentIntegrationOnboardingUrlRequest = {
    client: WebStripeClientType;
};

export type GetOrganizationPaymentIntegrationOnboardingUrlResponse = {
    onboardingUrl: string;
};

export type GetOrganizationPaymentIntegrationDashboardUrlResponse = {
    dashboardUrl: string;
};

export type UpdateOrganizationPaymentIntegrationImagesRequest = {
    logo: AvatarChange;
    icon: AvatarChange;
};

export type UpdateOrganizationPaymentIntegrationImagesResponse = {
    logo: 'success' | 'error';
    icon: 'success' | 'error';
};

export type GetOrganizationPaymentIntegrationIconOrLogoResponse = GetAvatarResponse | null;

type ActiveOrganizationClientRecordBillingDetails = {
    status: 'active';
    name: string;
    email: string;
    phoneNumber?: string;
};

type InsufficientInformationOrganizationClientRecordBillingDetails = {
    status: 'insufficient-information';
    name?: string;
    email?: string;
    phoneNumber?: string;
};

export type GetOrganizationClientRecordBillingDetailsResponse =
    | ActiveOrganizationClientRecordBillingDetails
    | InsufficientInformationOrganizationClientRecordBillingDetails;

export type UpdateOrganizationClientRecordBillingDetailsRequest = {
    name: string;
    email: string;
    phoneNumber?: string;
};

export type UpdateOrganizationClientRecordBillingDetailsResponse = ActiveOrganizationClientRecordBillingDetails;

export type OrganizationInvoiceItemDetails = Omit<OrganizationInvoiceItem, 'stripeCustomerId'>;

export type CreateOrganizationClientRecordInvoiceItemsRequest = {
    invoiceItems: Omit<OrganizationInvoiceItemDetails, 'invoiceItemId' | 'stripeCustomerId'>[];
};

export type CreateOrganizationClientRecordInvoiceItemsResponse = {
    items: OrganizationInvoiceItemDetails[];
};

export type ListOrganizationClientRecordInvoiceItemsResponse = {
    items: OrganizationInvoiceItemDetails[];
    subtotal: number;
};

export type GetOrganizationClientRecordInvoiceItemResponse = OrganizationInvoiceItemDetails;

export type UpdateOrganizationClientRecordInvoiceItemRequest = Omit<
    OrganizationInvoiceItemDetails,
    'invoiceItemId' | 'stripeCustomerId'
>;

export type UpdateOrganizationClientRecordInvoiceItemResponse = OrganizationInvoiceItemDetails;

export type CreateOrganizationClientRecordInvoiceRequest = {
    description?: string;
    daysUntilDue: number;
};

export type OrganizationInvoiceDetails = Omit<OrganizationInvoice, 'stripeCustomerId'>;
export type OrganizationInvoiceDetailsForStatus<T extends OrganizationInvoiceStatus> = Omit<
    OrganizationInvoiceForStatus<T>,
    'stripeCustomerId'
>;

export type CreateOrganizationClientRecordInvoiceResponse = OrganizationInvoiceDetailsForStatus<'open'>;

export type VoidOrganizationClientRecordInvoiceResponse = OrganizationInvoiceDetailsForStatus<'void'>;

export type MarkPaidOrganizationClientRecordInvoiceResponse = OrganizationInvoiceDetailsForStatus<'paid'>;

export type RefundOrganizationClientRecordInvoiceRequest = {
    reason: 'requested_by_customer' | 'duplicate';
};

export type RefundOrganizationClientRecordInvoiceResponse = OrganizationInvoiceDetailsForStatus<'refunded'>;

export type ListOrganizationClientRecordInvoicesResponse = {
    items: OrganizationInvoiceDetails[];
};

export type GetOrganizationClientRecordInvoiceResponse = OrganizationInvoiceDetails;

export type ListOrganizationClientRecordInvoiceItemsForInvoiceResponse = {
    items: OrganizationInvoiceItemDetails[];
};

export type ClientProfileNotificationCount = {
    organizationClientMedicalProfileId: string;
    notificationCount: number;
};

export type GetClientProfileNotificationCountsResponse = {
    counts: ClientProfileNotificationCount[];
};

export type OrganizationTableColumnFilterState = {
    selectedDiscreetValues?: string[];
};

export type OrganizationTableSortState = {
    columnKey: string;
    direction: 'asc' | 'desc';
};

export type OrganizationClientRecordsTableSettings = {
    columnFilters: Record<string, OrganizationTableColumnFilterState | undefined>;
    sortState?: OrganizationTableSortState;
};

export type GetOrganizationClientRecordsTableSettingsResponse = OrganizationClientRecordsTableSettings | null;

export type UpsertOrganizationClientRecordsTableSettingsRequest = OrganizationClientRecordsTableSettings;

export type UpsertOrganizationClientRecordsTableSettingsResponse = OrganizationClientRecordsTableSettings;
